import type { Image as ItkImage, TypedArray } from 'itk-wasm';

/**
 * Apply a binary mask to an itk-like volume and autocrop the result to the
 * bounding box of the masked region. Handles differing origins/spacings/directions
 * by mapping voxel centers through physical coordinates using the full direction matrix.
 *
 * The output volume uses the same spacing, direction, and coordinate system as
 * the input volume, with its origin shifted to the first included voxel so that
 * physical coordinates remain consistent.
 *
 * Returns null if either input lacks data, has zero spacing, or if the mask
 * contains no non-zero voxels within the volume extent.
 */
export function applyMaskToVolume(volume: ItkImage, mask: ItkImage): ItkImage | null {
    const components = volume.imageType.components || 1;

    if (!volume.data || !mask.data) {
        return null;
    }

    if (volume.spacing.some((s) => s === 0) || mask.spacing.some((s) => s === 0)) {
        return null;
    }

    const volumeDims = volume.size;
    const maskDims = mask.size;

    // Pre-compute the inverse direction matrix for the volume so we can map
    // physical coordinates back to volume index space.
    // direction is always a Float64Array in practice (see itk-wasm Image constructor)
    const volDirInv = invertDirection3x3(volume.direction as Float64Array);

    // --- Pass 1: find bounding box of masked region in volume index space ---
    let minX = volumeDims[0];
    let minY = volumeDims[1];
    let minZ = volumeDims[2];
    let maxX = -1;
    let maxY = -1;
    let maxZ = -1;

    for (let mz = 0; mz < maskDims[2]; mz++) {
        for (let my = 0; my < maskDims[1]; my++) {
            for (let mx = 0; mx < maskDims[0]; mx++) {
                const maskIndex = mx + my * maskDims[0] + mz * maskDims[0] * maskDims[1];
                if (mask.data[maskIndex] <= 0) continue;

                const [physX, physY, physZ] = indexToPhysical(mx, my, mz, mask);

                const [vx, vy, vz] = physicalToIndex(physX, physY, physZ, volume, volDirInv);

                if (
                    vx < 0 ||
                    vx >= volumeDims[0] ||
                    vy < 0 ||
                    vy >= volumeDims[1] ||
                    vz < 0 ||
                    vz >= volumeDims[2]
                ) {
                    continue;
                }

                minX = Math.min(minX, vx);
                minY = Math.min(minY, vy);
                minZ = Math.min(minZ, vz);
                maxX = Math.max(maxX, vx);
                maxY = Math.max(maxY, vy);
                maxZ = Math.max(maxZ, vz);
            }
        }
    }

    if (maxX < 0) {
        return null;
    }

    const cropDims: [number, number, number] = [maxX - minX + 1, maxY - minY + 1, maxZ - minZ + 1];

    const cropOrigin = indexToPhysical(minX, minY, minZ, volume);

    const ResultConstructor = volume.data.constructor as new (len: number) => TypedArray;
    const resultData = new ResultConstructor(cropDims[0] * cropDims[1] * cropDims[2] * components);

    // Pre-compute the inverse direction matrix for the mask for Pass 2.
    const maskDirInv = invertDirection3x3(mask.direction as Float64Array);

    // --- Pass 2: fill cropped buffer with masked volume data ---
    for (let z = minZ; z <= maxZ; z++) {
        for (let y = minY; y <= maxY; y++) {
            for (let x = minX; x <= maxX; x++) {
                const [physX, physY, physZ] = indexToPhysical(x, y, z, volume);

                const [maskX, maskY, maskZ] = physicalToIndex(physX, physY, physZ, mask, maskDirInv);

                if (
                    maskX < 0 ||
                    maskX >= maskDims[0] ||
                    maskY < 0 ||
                    maskY >= maskDims[1] ||
                    maskZ < 0 ||
                    maskZ >= maskDims[2]
                ) {
                    continue;
                }

                const maskIndex = maskX + maskY * maskDims[0] + maskZ * maskDims[0] * maskDims[1];
                if (mask.data[maskIndex] <= 0) continue;

                const cropVoxelIndex =
                    x - minX + (y - minY) * cropDims[0] + (z - minZ) * cropDims[0] * cropDims[1];
                const volVoxelIndex = x + y * volumeDims[0] + z * volumeDims[0] * volumeDims[1];

                const cropBase = cropVoxelIndex * components;
                const volBase = volVoxelIndex * components;
                for (let c = 0; c < components; c++) {
                    resultData[cropBase + c] = volume.data[volBase + c];
                }
            }
        }
    }

    return {
        imageType: { ...volume.imageType },
        data: resultData,
        size: [...cropDims],
        spacing: [...volume.spacing],
        origin: [...cropOrigin],
        direction: volume.direction.slice(),
    } as ItkImage;
}

// ---------------------------------------------------------------------------
// Coordinate helpers (direction-aware index <-> physical mapping)
// ---------------------------------------------------------------------------

type Direction3x3 = [number, number, number, number, number, number, number, number, number];

/** Convert voxel indices to physical (world) coordinates using the ITK formula:
 *  physical = origin + direction * (index * spacing)  */
function indexToPhysical(i: number, j: number, k: number, image: ItkImage): [number, number, number] {
    const d = image.direction as unknown as ArrayLike<number>;
    const sx = i * image.spacing[0];
    const sy = j * image.spacing[1];
    const sz = k * image.spacing[2];
    return [
        image.origin[0] + d[0] * sx + d[1] * sy + d[2] * sz,
        image.origin[1] + d[3] * sx + d[4] * sy + d[5] * sz,
        image.origin[2] + d[6] * sx + d[7] * sy + d[8] * sz,
    ];
}

/** Convert physical (world) coordinates to the nearest voxel indices:
 *  index = round( (directionInverse * (physical - origin)) / spacing )  */
function physicalToIndex(
    px: number,
    py: number,
    pz: number,
    image: ItkImage,
    dirInv: Direction3x3
): [number, number, number] {
    const dx = px - image.origin[0];
    const dy = py - image.origin[1];
    const dz = pz - image.origin[2];
    return [
        Math.round((dirInv[0] * dx + dirInv[1] * dy + dirInv[2] * dz) / image.spacing[0]),
        Math.round((dirInv[3] * dx + dirInv[4] * dy + dirInv[5] * dz) / image.spacing[1]),
        Math.round((dirInv[6] * dx + dirInv[7] * dy + dirInv[8] * dz) / image.spacing[2]),
    ];
}

/** Invert a row-major 3x3 direction matrix. */
function invertDirection3x3(dir: ArrayLike<number>): Direction3x3 {
    const [a, b, c, d, e, f, g, h, i] = dir as unknown as Direction3x3;
    const det = a * (e * i - f * h) - b * (d * i - f * g) + c * (d * h - e * g);
    const invDet = 1 / det;
    return [
        (e * i - f * h) * invDet,
        (c * h - b * i) * invDet,
        (b * f - c * e) * invDet,
        (f * g - d * i) * invDet,
        (a * i - c * g) * invDet,
        (c * d - a * f) * invDet,
        (d * h - e * g) * invDet,
        (b * g - a * h) * invDet,
        (a * e - b * d) * invDet,
    ];
}
